//src/components/Sidebar.tsx
import React, { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Иконки + кастомные компоненты
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Icon from "../UI/Icon/Icon.tsx";
import { ReactComponent as CircleDashed } from "../icons/CircleDashed.svg";
import { ReactComponent as ArrowSquareDownSvg } from "../icons/ArrowSquareDown.svg";
import { ReactComponent as Burger } from "../icons/Burger.svg";
import { ReactComponent as Divisions } from "../icons/Divisions.svg";
import { ReactComponent as Nomenclature } from "../icons/Nomenclature.svg";
import { ReactComponent as Planning } from "../icons/Planning.svg";
import { ReactComponent as Tech_Card } from "../icons/Tech-Card.svg";
import { ReactComponent as Users } from "../icons/Users.svg";
import { ReactComponent as Equipment } from "../icons/Equipment.svg";

import {
  SidebarContainer,
  SidebarHeader,
  ToggleButton,
  MenuList,
  MenuItemWrapper,
  LogoHeading,
  MenuItemText,
  LogoIcon,
  MenuListSupport
} from "./SidebarStyles.js";

// Описываем тип пункта меню
interface MenuItem {
  label: string;
  icon: ReactNode;  // ReactNode, потому что может быть <Icon> или <HomeIcon> ...
  link: string;
}

// Описываем пропсы компонента Sidebar
interface SidebarProps {
  enterpriseId: string;
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: FC<SidebarProps> = ({ enterpriseId, isLocked, isOpen, onToggle }) => {
  const { t } = useTranslation();

  const menuItems: MenuItem[] = [
    {
      label: t("orders"),
      icon: <Icon SvgComponent={ArrowSquareDownSvg} size={24} />,
      link: `/${enterpriseId}/orders`,
    },
    {
      label: t("sidebar.manage_nomenclature"),
      icon: <Icon SvgComponent={Nomenclature} size={24} />,
      link: `/${enterpriseId}/directory/nomenclature`,
    },
    {
      label: t("sidebar.manage_technology_cards"),
      icon: <Icon SvgComponent={Tech_Card} size={24} />,
      link: `/${enterpriseId}/directory/technology-card`,
    },
    {
      label: t("sidebar.manual_planning"),
      icon: <Icon SvgComponent={Planning} size={24} />,
      link: `/${enterpriseId}/directory/manual-planning`,
    },
    {
      label: t("sidebar.manage_divisions"),
      icon: <Icon SvgComponent={Divisions} size={24} />,
      link: `/${enterpriseId}/directory/divisions`,
    },
    {
      label: t("sidebar.users"),
      icon: <Icon SvgComponent={Users} size={24} />,
      link: `/${enterpriseId}/directory/users`,
    },
    {
      label: t("sidebar.manage_equipment"),
      icon: <Icon SvgComponent={Equipment} size={24} />,
      link: `/${enterpriseId}/directory/equipment`,
    },
    {
      label: t("sidebar.manage_equipment_group"),
      icon: <Icon SvgComponent={Equipment} size={24} />,
      link: `/${enterpriseId}/directory/equipment-group`,
    },
    {
      label: t("sidebar.home"),
      icon: <HomeIcon />,
      link: `/${enterpriseId}`,
    },
    {
      label: t("sidebar.login"),
      icon: <SettingsIcon />,
      link: `/${enterpriseId}/login`,
    },
    {
      label: t("sidebar.registration"),
      icon: <SettingsIcon />,
      link: `/${enterpriseId}/invitation-register`,
    },
    {
      label: "Reset Password",
      icon: <SettingsIcon />,
      link: `/${enterpriseId}/reset-password`,
    },
    {
      label: "Profile",
      icon: <SettingsIcon />,
      link: `/${enterpriseId}/profile`,
    },
  ];

  return (
    <SidebarContainer isOpen={isOpen}>
      <SidebarHeader>
        <ToggleButton onClick={onToggle}>
          <Icon SvgComponent={Burger} size={24} />
        </ToggleButton>
        {isOpen && (
          <LogoHeading variant="h5">
            PLAN.ER
            <LogoIcon SvgComponent={CircleDashed} size={24} />
          </LogoHeading>
        )}
      </SidebarHeader>

      <MenuList>
        {menuItems.map((item, idx) => (
          <MenuItemWrapper
            key={idx}
            to={item.link}
            disabled={isLocked}
            isOpen={isOpen}
          >
            {item.icon}
            {isOpen && <MenuItemText>{item.label}</MenuItemText>}
          </MenuItemWrapper>
        ))}
      </MenuList>

      <MenuListSupport>
        <MenuItemWrapper to="/support" isOpen={isOpen}>
          <HelpOutlineIcon />
          {isOpen && <MenuItemText>{t("sidebar.support")}</MenuItemText>}
        </MenuItemWrapper>
      </MenuListSupport>
    </SidebarContainer>
  );
};

export default Sidebar;
